/* Maison Joard — product page behaviours (sections/joard-product-info.liquid)
   Progressive enhancement over the plain cart add form: gallery swap, variant radiogroup,
   quantity stepper with live total, asynchronous add that re-renders Dawn's cart drawer. */

(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- money: reuse the shop money format passed from Liquid ---------- */
  function formatMoney(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }
    const value = Math.round(Number(cents) || 0);
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = (format || '{{ amount }}').match(placeholder);
    const type = match ? match[1] : 'amount';

    function withDelimiters(number, precision, thousands, decimal) {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return precision > 0 ? whole + decimal + parts[1] : whole;
    }

    let amount;
    switch (type) {
      case 'amount_no_decimals':
        amount = withDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amount = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        amount = withDelimiters(value, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = withDelimiters(value, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = withDelimiters(value, 2, "'", '.');
        break;
      case 'amount_with_period_and_space_separator':
        amount = withDelimiters(value, 2, ' ', '.');
        break;
      default:
        amount = withDelimiters(value, 2, ',', '.');
    }
    return (format || '{{ amount }}').replace(placeholder, amount);
  }

  /* ---------- gallery: scroll-snap carousel with arrows, thumbnails, keyboard and variant images ---------- */
  function initGallery(root) {
    const track = root.querySelector('[data-pdp-track]');
    if (!track) return;
    const slides = Array.from(track.querySelectorAll('[data-pdp-slide]'));
    if (slides.length < 2) return;
    const thumbs = Array.from(root.querySelectorAll('[data-pdp-thumb]'));
    const prev = root.querySelector('[data-pdp-prev]');
    const next = root.querySelector('[data-pdp-next]');
    const status = root.querySelector('[data-pdp-slide-status]');
    const smooth = reduceMotion ? 'auto' : 'smooth';
    let current = 0;

    function goTo(index, behavior) {
      const target = (index + slides.length) % slides.length;
      track.scrollTo({ left: slides[target].offsetLeft - track.offsetLeft, behavior: behavior || smooth });
      setCurrent(target);
    }

    function setCurrent(index) {
      if (index === current && thumbs.length && thumbs[index].classList.contains('is-active')) return;
      current = index;
      thumbs.forEach((b, i) => {
        b.classList.toggle('is-active', i === index);
        b.setAttribute('aria-pressed', i === index ? 'true' : 'false');
      });
      if (status) status.textContent = slides[index].getAttribute('aria-label') || '';
    }

    let ticking = false;
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const index = Math.round(track.scrollLeft / track.clientWidth);
        setCurrent(Math.max(0, Math.min(slides.length - 1, index)));
        ticking = false;
      });
    });

    if (prev) prev.addEventListener('click', () => goTo(current - 1));
    if (next) next.addEventListener('click', () => goTo(current + 1));
    thumbs.forEach((btn) => btn.addEventListener('click', () => goTo(parseInt(btn.dataset.index, 10) || 0)));
    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); goTo(current + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(current - 1); }
      if (event.key === 'Home') { event.preventDefault(); goTo(0); }
      if (event.key === 'End') { event.preventDefault(); goTo(slides.length - 1); }
    });

    /* follow the selected variant's image */
    root.querySelectorAll('[data-pdp-variant]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.variantImage;
        if (!id) return;
        const index = slides.findIndex((slide) => slide.dataset.imageId === id);
        if (index >= 0) goTo(index);
      });
    });

    /* keep the active slide aligned after a resize */
    window.addEventListener('resize', () => goTo(current, 'auto'));

    /* mouse drag: touch already pans natively through scroll snap */
    let drag = null;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { startX: event.clientX, startLeft: track.scrollLeft, moved: false };
      track.classList.add('is-dragging');
      track.setPointerCapture(event.pointerId);
    });
    track.addEventListener('pointermove', (event) => {
      if (!drag) return;
      const delta = event.clientX - drag.startX;
      if (Math.abs(delta) > 4) drag.moved = true;
      track.scrollLeft = drag.startLeft - delta;
    });
    function endDrag(event) {
      if (!drag) return;
      const delta = event.clientX - drag.startX;
      const threshold = track.clientWidth * 0.15;
      let target = Math.round(drag.startLeft / track.clientWidth);
      if (delta < -threshold) target += 1;
      if (delta > threshold) target -= 1;
      drag = null;
      track.classList.remove('is-dragging');
      goTo(Math.max(0, Math.min(slides.length - 1, target)));
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('click', (event) => { if (drag && drag.moved) event.preventDefault(); }, true);
  }

  /* ---------- description: four lines, then "See more" ---------- */
  function initDescription(root) {
    const wrap = root.querySelector('[data-pdp-desc-wrap]');
    if (!wrap) return;
    const desc = wrap.querySelector('[data-pdp-desc]');
    const toggle = wrap.querySelector('[data-pdp-desc-toggle]');
    const more = wrap.querySelector('[data-pdp-desc-more]');
    const less = wrap.querySelector('[data-pdp-desc-less]');
    if (!desc || !toggle) return;

    function measure() {
      if (toggle.getAttribute('aria-expanded') === 'true') return;
      desc.classList.add('is-clamped');
      const overflowing = desc.scrollHeight > desc.clientHeight + 2;
      toggle.hidden = !overflowing;
      if (!overflowing) desc.classList.remove('is-clamped');
    }
    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      desc.classList.toggle('is-clamped', expanded);
      if (more) more.hidden = !expanded;
      if (less) less.hidden = expanded;
    });
    measure();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    window.addEventListener('resize', measure);
  }

  /* ---------- floating add to cart: shows once the main button has left the viewport ---------- */
  function initStickyBar(root) {
    const bar = root.querySelector('[data-pdp-bar]');
    const addBtn = root.querySelector('[data-pdp-add]');
    if (!bar || !addBtn || !('IntersectionObserver' in window)) return;
    const barAdd = bar.querySelector('[data-pdp-bar-add]');
    const barLabel = bar.querySelector('[data-pdp-bar-label]');
    const addLabel = root.querySelector('[data-pdp-add-label]');

    let pastButton = false;
    let footerInView = false;
    function apply() {
      const visible = pastButton && !footerInView;
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (barAdd) barAdd.tabIndex = visible ? 0 : -1;
    }
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      /* visible only after the button has scrolled above the viewport, not before the visitor reaches it */
      pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      apply();
    }, { threshold: 0 });
    observer.observe(addBtn);
    /* and never over the footer, which it would cover */
    const footer = document.querySelector('.joard-footer, footer');
    if (footer) {
      new IntersectionObserver((entries) => { footerInView = entries[0].isIntersecting; apply(); }, { threshold: 0 }).observe(footer);
    }

    /* mirror the main button: label (total, adding, added) and availability */
    function mirror() {
      if (barLabel && addLabel) barLabel.textContent = addLabel.textContent.trim();
      if (barAdd) barAdd.disabled = addBtn.disabled;
    }
    new MutationObserver(mirror).observe(addBtn, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });
    mirror();
  }

  /* ---------- buy box: variants, quantity, total, asynchronous add ---------- */
  function initBuyBox(root) {
    const form = root.querySelector('[data-joard-pdp-form]');
    if (!form) return;

    const moneyFormat = form.dataset.moneyFormat || '{{ amount }}';
    const strings = {
      add: form.dataset.labelAdd || 'Add to cart',
      adding: form.dataset.labelAdding || '…',
      added: form.dataset.labelAdded || 'Added to cart',
      soldOut: form.dataset.labelSoldOut || 'Sold out',
      error: form.dataset.labelError || 'Could not add to cart.',
    };

    const qtyInput = form.querySelector('[data-qty-input]');
    const dec = form.querySelector('[data-qty-dec]');
    const inc = form.querySelector('[data-qty-inc]');
    const addBtn = form.querySelector('[data-pdp-add]');
    const addLabel = form.querySelector('[data-pdp-add-label]');
    const variantInput = form.querySelector('[data-pdp-variant-input]');
    const variantBtns = Array.from(form.querySelectorAll('[data-pdp-variant]'));
    const priceCurrent = root.querySelector('[data-pdp-price-current]');
    const compareWrap = root.querySelector('[data-pdp-compare]');
    const compareValue = root.querySelector('[data-pdp-compare-value]');
    const unitWrap = root.querySelector('[data-pdp-unit]');
    const unitPrice = root.querySelector('[data-pdp-unit-price]');
    const unitMeasure = root.querySelector('[data-pdp-unit-measure]');
    const status = root.querySelector('[data-pdp-status]');
    const errorBox = form.querySelector('[data-pdp-error]');

    let unitCents = parseInt(form.dataset.price, 10) || 0;
    let available = form.dataset.available === 'true';

    function quantity() {
      return Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
    }

    function renderAddLabel() {
      if (!addBtn || !addLabel) return;
      if (!available) {
        addBtn.disabled = true;
        addLabel.textContent = strings.soldOut;
        return;
      }
      addBtn.disabled = false;
      addLabel.textContent = '';
      addLabel.append(strings.add + ' — ');
      const total = document.createElement('span');
      total.setAttribute('data-pdp-add-total', '');
      total.textContent = formatMoney(unitCents * quantity(), moneyFormat);
      addLabel.append(total);
    }

    function setQuantity(value) {
      if (!qtyInput) return;
      qtyInput.value = Math.max(1, value);
      renderAddLabel();
    }
    if (dec) dec.addEventListener('click', () => setQuantity(quantity() - 1));
    if (inc) inc.addEventListener('click', () => setQuantity(quantity() + 1));
    if (qtyInput) {
      qtyInput.addEventListener('input', renderAddLabel);
      qtyInput.addEventListener('change', () => setQuantity(quantity()));
    }

    /* Variant radiogroup */
    function selectVariant(btn, focus) {
      if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
      variantBtns.forEach((b) => {
        const checked = b === btn;
        b.classList.toggle('is-selected', checked);
        b.setAttribute('aria-checked', checked ? 'true' : 'false');
        b.tabIndex = checked ? 0 : -1;
      });
      if (focus) btn.focus();

      const id = btn.dataset.variantId;
      if (variantInput) variantInput.value = id;
      unitCents = parseInt(btn.dataset.variantPrice, 10) || 0;
      available = btn.dataset.variantAvailable === 'true';
      const compare = parseInt(btn.dataset.variantCompareAt, 10) || 0;

      if (priceCurrent) priceCurrent.textContent = formatMoney(unitCents, moneyFormat);
      if (compareWrap) {
        const onSale = compare > unitCents;
        compareWrap.hidden = !onSale;
        if (compareValue) compareValue.textContent = onSale ? formatMoney(compare, moneyFormat) : '';
      }
      if (unitWrap) {
        const unitCentsValue = parseInt(btn.dataset.variantUnitPrice, 10);
        const measure = btn.dataset.variantUnitMeasure || '';
        const hasUnit = !isNaN(unitCentsValue) && measure !== '';
        unitWrap.hidden = !hasUnit;
        if (hasUnit) {
          if (unitPrice) unitPrice.textContent = formatMoney(unitCentsValue, moneyFormat);
          if (unitMeasure) unitMeasure.textContent = measure;
        }
      }
      renderAddLabel();

      const url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      window.history.replaceState({}, '', url);
    }

    if (variantBtns.length) {
      const enabled = () => variantBtns.filter((b) => b.getAttribute('aria-disabled') !== 'true');
      const current = variantBtns.find((b) => b.getAttribute('aria-checked') === 'true') || enabled()[0];
      variantBtns.forEach((b) => { b.tabIndex = b === current ? 0 : -1; });

      variantBtns.forEach((btn) => {
        btn.addEventListener('click', () => selectVariant(btn, false));
        btn.addEventListener('keydown', (event) => {
          const list = enabled();
          const index = list.indexOf(btn);
          let next = null;
          switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
              next = list[(index + 1) % list.length];
              break;
            case 'ArrowLeft':
            case 'ArrowUp':
              next = list[(index - 1 + list.length) % list.length];
              break;
            case 'Home':
              next = list[0];
              break;
            case 'End':
              next = list[list.length - 1];
              break;
            case ' ':
            case 'Enter':
              next = btn;
              break;
            default:
              return;
          }
          event.preventDefault();
          selectVariant(next, next !== btn);
        });
      });
    }

    /* Asynchronous add: re-render Dawn's cart drawer with the returned sections */
    function announce(text) {
      if (!status) return;
      status.textContent = '';
      window.setTimeout(() => { status.textContent = text; }, 50);
    }
    function showError(message) {
      if (!errorBox) return;
      errorBox.textContent = message;
      errorBox.hidden = false;
      errorBox.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
    }
    function clearError() {
      if (errorBox) { errorBox.hidden = true; errorBox.textContent = ''; }
    }

    form.addEventListener('submit', (event) => {
      if (!window.fetch || !window.FormData) return; /* plain POST fallback */
      event.preventDefault();
      if (!addBtn || addBtn.disabled) return;
      clearError();

      const drawer = document.querySelector('cart-drawer');
      const body = new FormData(form);
      const sections = drawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map((section) => section.id)
        : [];
      if (sections.length) {
        body.append('sections', sections.join(','));
        body.append('sections_url', window.location.pathname);
      }
      if (drawer && typeof drawer.setActiveElement === 'function') drawer.setActiveElement(addBtn);

      addBtn.disabled = true;
      addBtn.setAttribute('aria-busy', 'true');
      addLabel.textContent = strings.adding;

      fetch(form.action, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || data.status) {
            const message = data.description || data.message || strings.error;
            showError(message);
            announce(message);
            return;
          }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'joard-product',
              productVariantId: body.get('id'),
              cartData: data,
            });
          }
          if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
            drawer.renderContents(data); /* Dawn re-renders and opens the drawer */
          } else if (drawer && typeof drawer.open === 'function') {
            drawer.open(addBtn);
          }
          announce(strings.added);
          addLabel.textContent = strings.added;
          window.setTimeout(renderAddLabel, 1400);
        })
        .catch(() => {
          showError(strings.error);
          announce(strings.error);
        })
        .finally(() => {
          addBtn.removeAttribute('aria-busy');
          if (addLabel.textContent !== strings.added) renderAddLabel();
        });
    });
  }

  /* ---------- promises under the buy box: one at a time, dots on the right ---------- */
  function initAssure(root) {
    const wrap = root.querySelector('[data-pdp-assure]');
    if (!wrap) return;
    const items = Array.from(wrap.querySelectorAll('[data-pdp-assure-item]'));
    const dots = Array.from(wrap.querySelectorAll('[data-pdp-assure-dot]'));
    if (items.length < 2) return;
    let index = 0;
    let timer = null;
    const show = (i) => {
      const next = (i + items.length) % items.length;
      if (next === index) return;
      const leaving = items[index];
      index = next;
      items.forEach((el, n) => {
        el.classList.toggle('is-active', n === index);
        el.classList.toggle('is-leaving', el === leaving);
      });
      /* once out of view, the leaving one goes back to its waiting place on the right */
      window.setTimeout(() => leaving.classList.remove('is-leaving'), reduceMotion ? 0 : 700);
      dots.forEach((d, n) => {
        d.classList.toggle('is-active', n === index);
        if (n === index) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    };
    /* WCAG 2.2.2: a pause button stops the rotation for good; hover and focus only hold it */
    const toggle = wrap.querySelector('[data-pdp-assure-toggle]');
    let paused = reduceMotion;
    const stop = () => { if (timer) clearInterval(timer); timer = null; };
    const start = () => { if (paused || timer || document.hidden) return; timer = setInterval(() => show(index + 1), 4500); };
    const syncToggle = () => {
      if (!toggle) return;
      toggle.classList.toggle('is-paused', paused);
      toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
    };
    if (toggle) toggle.addEventListener('click', () => { paused = !paused; syncToggle(); if (paused) stop(); else start(); });
    syncToggle();
    dots.forEach((d) => d.addEventListener('click', () => { show(Number(d.dataset.pdpAssureDot)); stop(); start(); }));
    wrap.addEventListener('mouseenter', stop);
    wrap.addEventListener('mouseleave', start);
    wrap.addEventListener('focusin', stop);
    wrap.addEventListener('focusout', (event) => { if (!wrap.contains(event.relatedTarget)) start(); });
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
    start();
  }

  function init() {
    document.querySelectorAll('[data-joard-pdp]').forEach((root) => {
      if (root.dataset.joardPdpReady) return;
      root.dataset.joardPdpReady = 'true';
      initGallery(root);
      initBuyBox(root);
      initDescription(root);
      initStickyBar(root);
      initAssure(root);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
